import XLSX from "xlsx";
import filedownload from "js-file-download";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { DimensionHelper, LocalizationHelperInstance } from "utils/helpers";
import { TranslationKeys } from "translations";

const ExportHelper = {
  generateSheetHeaders(
    reportRows: WintSuperkollAPIModelFinancialReport.FinancialReportRow[],
    columnToHeaders: (column: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn, index: number) => string[]
  ) {
    const firstRow = reportRows.find((row) => row.columns != null);

    return [
      [
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Name),
        ...(firstRow?.columns ?? []).flatMap(columnToHeaders),
      ],
    ];
  },

  generateSheetData(
    reportRows: WintSuperkollAPIModelFinancialReport.FinancialReportRow[],
    descriptionFormatter: (row: WintSuperkollAPIModelFinancialReport.FinancialReportRow) => string,
    rowToColumns: (column: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn, index: number) => any[]
  ) {
    function rowFormattingFunction(
      row: WintSuperkollAPIModelFinancialReport.FinancialReportRow,
      level: number
    ): any[][] {
      switch (row.type) {
        case WintSuperkollAPIModelFinancialReport.FinancialReportRowType.Category:
        case WintSuperkollAPIModelFinancialReport.FinancialReportRowType.Sum:
        case WintSuperkollAPIModelFinancialReport.FinancialReportRowType.SumSmall:
        case WintSuperkollAPIModelFinancialReport.FinancialReportRowType.SumNoFormatting:
          return [
            ["> ".repeat(level) + descriptionFormatter(row), ...(row.columns?.flatMap(rowToColumns) ?? [])],
          ].concat(row.children?.flatMap((row) => rowFormattingFunction(row, level + 1)) ?? []);

        case WintSuperkollAPIModelFinancialReport.FinancialReportRowType.Title:
          return [[row.description]];

        case WintSuperkollAPIModelFinancialReport.FinancialReportRowType.Empty:
          return [[]];

        default:
          return [[]];
      }
    }

    return reportRows.flatMap((row) => rowFormattingFunction(row, 0));
  },

  getDimensionTypesById(allDimensionTypes: any[] | undefined) {
    return new Map((allDimensionTypes ?? []).map((d) => [d.id, d.type]));
  },

  getAllDimensionTypeNamesHeaders(
    items: any[],
    dimensionTypesById: Map<any, any>
  ): string[] {
    return Array.from(
      new Set(
        (items as any[])
          .flatMap((t) =>
            (t?.dimensions ?? [])
              .map((d: any) =>
                DimensionHelper.getDimensionTypeText(
                  dimensionTypesById.get(d.typeId) ?? 0,
                  d?.typeName
                )
              )
              .filter((v: any) => !!v)
          )
      )
    ).sort();
  },

  buildDimensionValues(
    item: any,
    allDimensionTypeNames: string[],
    dimensionTypesById: Map<any, any>
  ): string[] {
    const dimensionsByTypeName = new Map<string, string[]>();
    (item?.dimensions ?? []).forEach((d: any) => {
      const header = DimensionHelper.getDimensionTypeText(
        dimensionTypesById.get(d.typeId) ?? 0,
        d?.typeName
      );
      const value = d?.name ?? "";
      if (!header) return;
      const existing = dimensionsByTypeName.get(header) ?? [];
      if (value !== "") existing.push(value);
      dimensionsByTypeName.set(header, existing);
    });

    return allDimensionTypeNames.map((typeName) => {
      const names = dimensionsByTypeName.get(typeName) ?? [];
      return names.join(", ");
    });
  },

  exportReport(data: any[][], filename: string, sheetTitle?: string) {
    const book = XLSX.utils.book_new();
    const sheet = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(book, sheet, sheetTitle);
    if (filename.toLowerCase().endsWith(".csv")) {
      // The built-in CSV writer leaves the last row unterminated; downstream tools expect every row to end with a newline, so write the CSV ourselves with the same UTF-8 BOM.
      const csv: string = XLSX.write(book, { bookType: "csv", type: "string" });
      filedownload(`﻿${csv}${csv ? "\n" : ""}`, filename, "text/csv;charset=utf-8");
      return;
    }
    XLSX.writeFile(book, filename);
  },
};

export default ExportHelper;
